import { strict as assert } from 'assert';
import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec3,
} from 'gl-matrix';
import { BoundingBox } from './BoundingBox';
import { Vertex, VerticesToArrays } from './slicemesh/Vertex';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { tesselateConvexPolygon } from './tesselateConvexPolygon';
import { gl } from '~/engine/Engine';
import { areParallelDirections } from '~/domain/viewport/frameRotation';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { Edge } from './Edge';
import { RotatedBoundingBox } from './RotatedBoundingBox';

/**
 * Index of a grid axis: 0 = X, 1 = Y, 2 = Z (the slice axis).
 */
export type SliceAxis = 0 | 1 | 2;

/**
 * Represents a Grid like object dimensions in 3D.
 */
export class Grid {
    private readonly size: ReadonlyVec3;
    private readonly res: ReadonlyVec3;
    private readonly transformation: ReadonlyMat4;
    private readonly _voxelToWorld: ReadonlyMat4;
    private readonly _worldToVoxel: ReadonlyMat4;
    private readonly _voxelToClipSpace: ReadonlyMat4;
    private readonly _worldToClipSpace: ReadonlyMat4;
    private readonly _worldToTextureUV: ReadonlyMat4;

    /**
     * Construct a new Grid object
     *
     * @param sizex - X size in voxels, must be >= 0 and integer
     * @param sizey - Y size in voxels, must be >= 0 and integer
     * @param sizez - Z size in voxels, must be >= 0 and integer
     * @param resx - X resolution in mm, must be >= 0
     * @param resy - Y resolution in mm, must be >= 0
     * @param resz - Z resolution in mm, must be >= 0
     * @param transformation - transformation from grid coordinates to PatientSpace.\
     * Should include rotation and translation, scaling is derived from resolution.
     * Translation embedded in the transformation is to the corner of the first voxel. If null, an identity transformation
     * is used.
     */
    constructor(
        sizex: number,
        sizey: number,
        sizez: number,
        resx: number,
        resy: number,
        resz: number,
        transformation: ReadonlyMat4 | null
    ) {
        assert(Number.isInteger(sizex), 'Grid X size must be integer');
        assert(Number.isInteger(sizey), 'Grid Y size must be integer');
        assert(Number.isInteger(sizez), 'Grid Z size must be integer');
        assert(sizex >= 0, 'Grid X size must be non-negative');
        assert(sizey >= 0, 'Grid Y size must be non-negative');
        assert(sizez >= 0, 'Grid Z size must be non-negative');
        assert(resx >= 0, 'Grid X resolution must be non-negative');
        assert(resy >= 0, 'Grid Y resolution must be non-negative');
        assert(resz >= 0, 'Grid Z resolution must be non-negative');
        this.size = vec3.fromValues(sizex, sizey, sizez);
        this.res = vec3.fromValues(resx, resy, resz);

        this.transformation = transformation
            ? mat4.clone(transformation)
            : mat4.create();

        this._voxelToWorld = mat4.multiply(
            mat4.create(),
            this.transformation,
            mat4.fromScaling(mat4.create(), this.res)
        );
        this._worldToVoxel = mat4.invert(mat4.create(), this._voxelToWorld);

        // For empty grids, use identity matrices as fallback
        if (sizex <= 0 || sizey <= 0 || sizez <= 0) {
            this._worldToTextureUV = mat4.create();
            this._worldToClipSpace = mat4.create();
            this._voxelToClipSpace = mat4.create();
            return;
        }

        // worldToTextureUV maps world coordinates to [0, 1] texture UV space
        // textureUV = voxel / size, so worldToTextureUV = scale(1/size) * worldToVoxel
        const voxelToTextureUV = mat4.fromScaling(
            mat4.create(),
            vec3.fromValues(1 / sizex, 1 / sizey, 1 / sizez)
        );
        this._worldToTextureUV = mat4.multiply(
            mat4.create(),
            voxelToTextureUV,
            this._worldToVoxel
        );

        // worldToClipSpace maps world coordinates to [-1, 1] clip space
        // clipSpace = textureUV * 2 - 1
        const textureUVToClipSpace = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.create(),
            vec3.fromValues(-1, -1, -1),
            vec3.fromValues(2, 2, 2)
        );
        this._worldToClipSpace = mat4.multiply(
            mat4.create(),
            textureUVToClipSpace,
            this._worldToTextureUV
        );

        // voxelToClipSpace maps voxel coordinates directly to [-1, 1]
        // clip space, avoiding the intermediate world-space step.
        this._voxelToClipSpace = mat4.multiply(
            mat4.create(),
            this._worldToClipSpace,
            this._voxelToWorld
        );
    }

    public static fromVoxelToWorld(size: vec3, voxelToWorld: mat4) {
        const res = [
            vec3.length([voxelToWorld[0], voxelToWorld[1], voxelToWorld[2]]),
            vec3.length([voxelToWorld[4], voxelToWorld[5], voxelToWorld[6]]),
            vec3.length([voxelToWorld[8], voxelToWorld[9], voxelToWorld[10]]),
        ];

        const transform = mat4.clone(voxelToWorld);
        // remove scale
        transform[0] /= res[0];
        transform[1] /= res[0];
        transform[2] /= res[0];

        transform[4] /= res[1];
        transform[5] /= res[1];
        transform[6] /= res[1];

        transform[8] /= res[2];
        transform[9] /= res[2];
        transform[10] /= res[2];

        return new Grid(
            size[0],
            size[1],
            size[2],
            res[0],
            res[1],
            res[2],
            transform
        );
    }

    /**
     * A grid of the given sampling and orientation whose box is centered
     * on a point.
     *
     * @param center PatientSpace center of the grid's box
     * @param orientation Rotation from the grid frame to PatientSpace
     * @param sizeVoxels Number of voxels along each grid axis
     * @param resolutionMm Voxel spacing in mm along each grid axis
     */
    public static centeredOn(
        center: ReadonlyVec3,
        orientation: ReadonlyMat3,
        sizeVoxels: ReadonlyVec3,
        resolutionMm: ReadonlyVec3
    ): Grid {
        const halfExtent = vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(
                0.5 * sizeVoxels[0] * resolutionMm[0],
                0.5 * sizeVoxels[1] * resolutionMm[1],
                0.5 * sizeVoxels[2] * resolutionMm[2]
            ),
            orientation
        );
        const corner = vec3.subtract(vec3.create(), center, halfExtent);
        const r = orientation;
        const transformation = mat4.fromValues(
            r[0],
            r[1],
            r[2],
            0,
            r[3],
            r[4],
            r[5],
            0,
            r[6],
            r[7],
            r[8],
            0,
            corner[0],
            corner[1],
            corner[2],
            1
        );
        return new Grid(
            sizeVoxels[0],
            sizeVoxels[1],
            sizeVoxels[2],
            resolutionMm[0],
            resolutionMm[1],
            resolutionMm[2],
            transformation
        );
    }

    /**
     * Helper function to create an empty Grid object with default values
     * @param transformation optional transformation matrix, defaults to identity
     * @returns empty Grid with 0 size and 1mm resolution
     */
    public static empty(transform: ReadonlyMat4 | null = null): Grid {
        return new Grid(0, 0, 0, 1, 1, 1, transform);
    }

    /**
     * Helper function to create a Grid object from DICOM data
     * @param size grid size
     * @param res grid resolution
     * @param patientPos translation in patient space, DICOM Image Position (Patient) attribute.
     * @param orientation grid orientation matrix, the DICOM Image Orientation (Patient) attribute
     * @returns
     */
    public static fromDicom(
        size: ReadonlyVec3,
        res: ReadonlyVec3,
        patientPos: ReadonlyVec3,
        orientation: ReadonlyMat3
    ): Grid {
        // DICOM patient position is the center of the first voxel,
        // we need to convert it to the corner.
        // The half-voxel offset must be transformed by orientation
        // to correctly handle tilted/rotated images.
        const halfVoxelLocal = vec3.fromValues(
            res[0] / 2,
            res[1] / 2,
            res[2] / 2
        );
        const halfVoxelPatient = vec3.transformMat3(
            vec3.create(),
            halfVoxelLocal,
            orientation
        );
        const cornerTranslation = vec3.subtract(
            vec3.create(),
            patientPos,
            halfVoxelPatient
        );
        // Assemble the transformation directly from the DICOM direction
        // cosines — NOT via quat.fromMat3 + fromRotationTranslation.
        // The quaternion of a 90° rotation has irrational components
        // (±√0.5) that do not survive the float32 round-trip, so a
        // coronal-acquired series (IOP [1,0,0],[0,0,1]) reconstructed
        // its orientation as ~0.99999994/3.4e-8 instead of 1/0 and
        // {@link isAxisAligned} misclassified it as oblique. (The four
        // transversal patient positions are 180° rotations with exact float32
        // quaternions, which is why CT never exposed this.) Copying the
        // matrix verbatim keeps exact inputs exact and stays faithful
        // to the tag values for oblique ones.
        const transformation = mat4.fromValues(
            orientation[0],
            orientation[1],
            orientation[2],
            0,
            orientation[3],
            orientation[4],
            orientation[5],
            0,
            orientation[6],
            orientation[7],
            orientation[8],
            0,
            cornerTranslation[0],
            cornerTranslation[1],
            cornerTranslation[2],
            1
        );

        return new Grid(
            size[0],
            size[1],
            size[2],
            res[0],
            res[1],
            res[2],
            transformation
        );
    }

    public static clone(grid: Grid): Grid {
        return new Grid(
            grid.sizeX,
            grid.sizeY,
            grid.sizeZ,
            grid.resX,
            grid.resY,
            grid.resZ,
            grid.transformation
        );
    }

    /** Instance clone helper */
    public clone(): Grid {
        return Grid.clone(this);
    }

    // ── Aligned-grid helpers ────────────────────────────────────

    /**
     * Validate that two grids share the same orientation and
     * resolution (within tolerance). Throws if they differ.
     */
    private static assertAligned(a: Grid, b: Grid, caller: string): void {
        const oA = a.getOrientation();
        const oB = b.getOrientation();
        for (let i = 0; i < 9; i++) {
            if (Math.abs(oA[i] - oB[i]) > 1e-6) {
                throw new Error(`${caller}: orientations differ`);
            }
        }
        const rA = a.getRes();
        const rB = b.getRes();
        if (
            Math.abs(rA[0] - rB[0]) > 1e-6 ||
            Math.abs(rA[1] - rB[1]) > 1e-6 ||
            Math.abs(rA[2] - rB[2]) > 1e-6
        ) {
            throw new Error(`${caller}: resolutions differ`);
        }
    }

    /**
     * Return this grid's AABB in the orientation-aligned local mm
     * frame: `{min, max}` where min = `Rinv * position` and
     * max = min + getSizeMM().
     *
     * This frame is shared by all grids with the same orientation
     * and is the correct space for combining grid extents.
     */
    public getLocalBounds(): { min: vec3; max: vec3 } {
        const Rinv = this.getOrientationInverse();
        const localMin = vec3.transformMat3(
            vec3.create(),
            this.getPosition(),
            Rinv
        );
        const localMax = vec3.add(vec3.create(), localMin, this.getSizeMM());
        return { min: localMin, max: localMax };
    }

    /**
     * Build a new Grid from local-frame AABB bounds, using this
     * grid's orientation and resolution. Voxel counts are ceiled.
     */
    private static fromLocalBounds(
        minLocal: ReadonlyVec3,
        maxLocal: ReadonlyVec3,
        ref: Grid,
        rounding: 'ceil' | 'round' = 'round'
    ): Grid {
        const res = ref.getRes();
        const extent = vec3.sub(vec3.create(), maxLocal, minLocal);
        const roundFn = rounding === 'ceil' ? Math.ceil : Math.round;
        const sizeX = Math.max(1, roundFn(extent[0] / res[0]));
        const sizeY = Math.max(1, roundFn(extent[1] / res[1]));
        const sizeZ = Math.max(1, roundFn(extent[2] / res[2]));

        const R = ref.getOrientation();
        const origin = vec3.transformMat3(vec3.create(), minLocal, R);
        const transform = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), R),
            origin
        );

        return new Grid(sizeX, sizeY, sizeZ, res[0], res[1], res[2], transform);
    }

    /**
     * Compute the intersection of two aligned grids (same
     * orientation and resolution). Returns the overlapping
     * sub-region, or {@link Grid.empty} if there is no overlap.
     *
     * @throws Error if orientations or resolutions differ
     */
    public static intersectAligned(a: Grid, b: Grid): Grid {
        Grid.assertAligned(a, b, 'intersectAligned');

        const boundsA = a.getLocalBounds();
        const boundsB = b.getLocalBounds();

        // Component-wise intersection: max of mins, min of maxes
        const intMin = vec3.fromValues(
            Math.max(boundsA.min[0], boundsB.min[0]),
            Math.max(boundsA.min[1], boundsB.min[1]),
            Math.max(boundsA.min[2], boundsB.min[2])
        );
        const intMax = vec3.fromValues(
            Math.min(boundsA.max[0], boundsB.max[0]),
            Math.min(boundsA.max[1], boundsB.max[1]),
            Math.min(boundsA.max[2], boundsB.max[2])
        );

        if (
            intMin[0] >= intMax[0] ||
            intMin[1] >= intMax[1] ||
            intMin[2] >= intMax[2]
        ) {
            return Grid.empty(b.getTransformation());
        }

        const result = Grid.fromLocalBounds(intMin, intMax, b);
        return result.isEmpty ? Grid.empty(b.getTransformation()) : result;
    }

    /**
     * Compute the union of two aligned grids (same orientation
     * and resolution). Returns a new Grid covering both inputs,
     * optionally with additional margin in mm on all sides.
     *
     * @throws Error if orientations or resolutions differ
     */
    public static unionAligned(a: Grid, b: Grid, marginMm = 0): Grid {
        Grid.assertAligned(a, b, 'unionAligned');

        const boundsA = a.getLocalBounds();
        const boundsB = b.getLocalBounds();

        // Component-wise union: min of mins, max of maxes
        const uMin = vec3.fromValues(
            Math.min(boundsA.min[0], boundsB.min[0]),
            Math.min(boundsA.min[1], boundsB.min[1]),
            Math.min(boundsA.min[2], boundsB.min[2])
        );
        const uMax = vec3.fromValues(
            Math.max(boundsA.max[0], boundsB.max[0]),
            Math.max(boundsA.max[1], boundsB.max[1]),
            Math.max(boundsA.max[2], boundsB.max[2])
        );

        if (marginMm > 0) {
            const m = vec3.fromValues(marginMm, marginMm, marginMm);
            vec3.sub(uMin, uMin, m);
            vec3.add(uMax, uMax, m);
        }

        return Grid.fromLocalBounds(uMin, uMax, a, 'ceil');
    }

    /**
     * Creates a new Grid with an additional transformation applied.
     * The new transformation is applied after the current grid transformation,
     * effectively transforming the grid in world space.
     *
     * @param additionalTransform - Transformation matrix to apply to this grid
     * @returns New Grid with the combined transformation
     */
    public transform(additionalTransform: ReadonlyMat4): Grid {
        const newTransformation = mat4.multiply(
            mat4.create(),
            additionalTransform,
            this.transformation
        );
        return new Grid(
            this.sizeX,
            this.sizeY,
            this.sizeZ,
            this.resX,
            this.resY,
            this.resZ,
            newTransformation
        );
    }

    /**
     * Size of the Grid (x,y,z)
     * @returns Size as a {@link ReadonlyVec3}
     */
    getSize(): ReadonlyVec3 {
        return this.size;
    }

    get sizeX(): number {
        return this.size[0];
    }
    get sizeY(): number {
        return this.size[1];
    }
    get sizeZ(): number {
        return this.size[2];
    }

    /**
     * Resolution of the Grid (x,y,z)
     * @returns Resolution as a {@link ReadonlyVec3}
     */
    getRes(): ReadonlyVec3 {
        return this.res;
    }

    /**
     * Check if the grid is empty (has zero or negative dimensions)
     * @returns true if any dimension is zero or negative, false otherwise
     */
    get isEmpty(): boolean {
        return this.size[0] <= 0 || this.size[1] <= 0 || this.size[2] <= 0;
    }

    get resX(): number {
        return this.res[0];
    }
    get resY(): number {
        return this.res[1];
    }
    get resZ(): number {
        return this.res[2];
    }

    /**
     * Size of the Grid (x,y,z) in mm
     * @returns Size as a {@link ReadonlyVec3}
     */
    getSizeMM(): ReadonlyVec3 {
        return vec3.mul(vec3.create(), this.size, this.res);
    }

    /**
     * Get the X size in mm
     */
    get sizeXMM(): number {
        return this.size[0] * this.res[0];
    }

    /**
     * Get the Y size in mm
     */
    get sizeYMM(): number {
        return this.size[1] * this.res[1];
    }

    /**
     * Get the Z size in mm
     */
    get sizeZMM(): number {
        return this.size[2] * this.res[2];
    }

    /**
     * Position of the center of the grid in world coordinates.
     */
    get center(): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(
                this.size[0] / 2,
                this.size[1] / 2,
                this.size[2] / 2
            ),
            this.voxelToWorld
        );
    }

    /**
     * Get the transformation matrix of this Grid
     * @returns transformation matrix as a {@link ReadonlyMat4} or null if not defined
     */
    getTransformation(): ReadonlyMat4 {
        return this.transformation;
    }

    /**
     * Patient coordinates to Voxel coordinates transformation matrix
     */
    get worldToVoxel(): ReadonlyMat4 {
        return this._worldToVoxel;
    }

    /**
     * Voxel coordinates to Patient coordinates transformation matrix
     */
    get voxelToWorld(): ReadonlyMat4 {
        return this._voxelToWorld;
    }

    /**
     * Voxel to clip space [-1, 1] transformation matrix.
     * Use this for converting voxel coordinates (e.g. from
     * marching squares) directly to NDC without an
     * intermediate world-space step.
     */
    get voxelToClipSpace(): ReadonlyMat4 {
        return this._voxelToClipSpace;
    }

    /**
     * World to clip space [-1, 1] transformation matrix.
     * Use this for shader calculations that operate in normalized device coordinates.
     */
    get worldToClipSpace(): ReadonlyMat4 {
        return this._worldToClipSpace;
    }

    /**
     * World to texture UV [0, 1] transformation matrix.
     * Use this for OpenGL texture() sampling which expects [0, 1] coordinates.
     */
    get worldToTextureUV(): ReadonlyMat4 {
        return this._worldToTextureUV;
    }

    // Legacy method, should be removed once grids are not converted to bounding boxes.
    public toAxisAlignedBoundingBox(): BoundingBox {
        const bb = new BoundingBox();
        for (let dx = 0; dx <= 1; dx++) {
            for (let dy = 0; dy <= 1; dy++) {
                for (let dz = 0; dz <= 1; dz++) {
                    bb.expandWithPoint(
                        this.toWorldSpace(
                            vec3.fromValues(
                                dx * this.sizeX,
                                dy * this.sizeY,
                                dz * this.sizeZ
                            )
                        )
                    );
                }
            }
        }
        return bb;
    }

    public fromBoundingBox(bb: BoundingBox, size: ReadonlyVec3): Grid {
        if (size[0] <= 0 || size[1] <= 0 || size[2] <= 0) {
            return Grid.empty();
        }
        if (!bb.bounds) {
            return Grid.empty();
        }
        const res = vec3.div(vec3.create(), bb.size, size);
        return new Grid(
            size[0],
            size[1],
            size[2],
            res[0],
            res[1],
            res[2],
            mat4.fromTranslation(mat4.create(), bb.bounds.min)
        );
    }

    public toAxisAlignedGrid(size: ReadonlyVec3): Grid {
        const bb = this.toAxisAlignedBoundingBox();
        return this.fromBoundingBox(bb, size);
    }

    /**
     * @param voxel a point in voxel coordinates
     * @returns whether the given voxel coordinate is inside the grid bounds
     */
    includesVoxel(voxel: ReadonlyVec3): boolean {
        const intCoords = vec3.floor(vec3.create(), voxel);
        return (
            intCoords[0] >= 0 &&
            intCoords[1] >= 0 &&
            intCoords[2] >= 0 &&
            intCoords[0] < this.sizeX &&
            intCoords[1] < this.sizeY &&
            intCoords[2] < this.sizeZ
        );
    }

    /**
     * @param position a point in PatientSpace coordinates
     * @returns whether the given position is inside the grid bounds
     */
    includesPoint(position: ReadonlyVec3): boolean {
        const voxelCoords = this.toVoxelSpace(position);
        return this.includesVoxel(voxelCoords);
    }

    /**
     * Transform a position from PatientSpace to VoxelSpace
     * @param position
     * @returns
     */
    toVoxelSpace(position: ReadonlyVec3): vec3 {
        const voxelPos = vec3.transformMat4(
            vec3.create(),
            position,
            this.worldToVoxel
        );
        return voxelPos;
    }

    /**
     * Transform a position from VoxelSpace to PatientSpace
     * @param position
     * @returns
     */
    toWorldSpace(position: ReadonlyVec3): vec3 {
        const pos = vec3.transformMat4(
            vec3.create(),
            position,
            this.voxelToWorld
        );
        return pos;
    }

    /**
     * Transform a position from clip space [-1, 1] to PatientSpace
     * @param position a point in clip space coordinates
     * @returns position in PatientSpace
     */
    clipSpaceToWorldPosition(position: ReadonlyVec3): vec3 {
        const clipToWorld = mat4.invert(mat4.create(), this._worldToClipSpace);
        return vec3.transformMat4(vec3.create(), position, clipToWorld);
    }

    /**
     * Transform a position from voxel coordinates to clip space [-1, 1]
     * @param position a point in voxel coordinates
     * @returns position in clip space
     */
    voxelToClipSpacePosition(position: ReadonlyVec3): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            position,
            this._voxelToClipSpace
        );
    }

    /**
     * Transform a position from PatientSpace to clip space [-1, 1]
     * @param position a point in PatientSpace coordinates
     * @returns position in clip space
     */
    worldToClipSpacePosition(position: ReadonlyVec3): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            position,
            this._worldToClipSpace
        );
    }

    /**
     * Transform a position from PatientSpace to texture UV space [0, 1]
     * @param position a point in PatientSpace coordinates
     * @returns position in texture UV space for use with texture() sampling
     */
    worldToTextureUVPosition(position: ReadonlyVec3): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            position,
            this._worldToTextureUV
        );
    }

    /**
     * Transform a voxel position (center of voxel) to PatientSpace
     * @param voxelPos can be non-integer voxel coordinates
     * @returns voxel center position in PatientSpace
     */
    voxelCenterToWorldSpace(voxelPos: ReadonlyVec3): vec3 {
        const centerOffset = vec3.fromValues(0.5, 0.5, 0.5);
        const posWithOffset = vec3.add(vec3.create(), voxelPos, centerOffset);
        return this.toWorldSpace(posWithOffset);
    }

    /**
     * Extent (mm) of a single voxel projected onto `patientDir`: the support
     * width, along that direction, of the voxel box — equivalently, of the
     * neighbourhood of samples that interpolation blends into a value there.
     * The localization primitive: the image cannot distinguish positions
     * closer than this along `patientDir`.
     *
     * @param patientDir Direction in PatientSpace; need not be unit length.
     * @throws if `patientDir` has zero length.
     */
    public voxelExtentAlong(patientDir: ReadonlyVec3): number {
        if (!(vec3.length(patientDir) > 0)) {
            throw new Error(
                'voxelExtentAlong: direction must have non-zero length'
            );
        }
        const unit = vec3.normalize(vec3.create(), patientDir);
        return (
            this.resX * Math.abs(vec3.dot(this.getUnitX(), unit)) +
            this.resY * Math.abs(vec3.dot(this.getUnitY(), unit)) +
            this.resZ * Math.abs(vec3.dot(this.getUnitZ(), unit))
        );
    }

    /**
     * Effective voxel spacing along `patientDir`:
     * ‖voxelToWorldLinearᵀ · dir‖, the Euclidean norm of the per-axis
     * spacings projected onto the direction. Its square is a convex
     * combination of the squared axis resolutions (weighted by the squared
     * direction cosines), so for every direction it lies between the
     * finest and coarsest resolution, varies smoothly with the direction,
     * and equals the axis resolution exactly along each grid axis.
     * Geometrically the support width of the ellipsoid inscribed in the
     * voxel; never exceeds {@link voxelExtentAlong}, the voxel box itself.
     *
     * @param patientDir Direction in PatientSpace; need not be unit length.
     * @throws if `patientDir` has zero length.
     */
    public voxelSpacingAlong(patientDir: ReadonlyVec3): number {
        if (!(vec3.length(patientDir) > 0)) {
            throw new Error(
                'voxelSpacingAlong: direction must have non-zero length'
            );
        }
        const unit = vec3.normalize(vec3.create(), patientDir);
        const gridDir = vec3.transformMat3(
            unit,
            unit,
            this.getOrientationInverse()
        );
        return vec3.length(vec3.mul(gridDir, gridDir, this.res));
    }

    /**
     * Snap a patient-space position along one grid axis to the nearest
     * slice center on that axis, preserving its in-plane location on the
     * other two axes. Defaults to the slice (Z) axis.
     *
     * Use Math.floor on the voxel coordinate, not Math.round: a point
     * exactly on a slice plane (voxel coordinate = N) belongs to slice N,
     * not N + 1. Rounding-to-nearest in voxel-space treats N + 0.5 (slice
     * center) as already-rounded, which is fine, but N (slice boundary)
     * would tie-break to N + 1 on some engines — interpolating the slice
     * above the one the user is viewing.
     *
     * @param patientPos position in PatientSpace
     * @param axis grid axis to snap along (0 = X, 1 = Y, 2 = Z; default 2)
     * @returns PatientSpace position snapped to the slice center on `axis`
     */
    public snapPositionToSliceCenter(
        patientPos: ReadonlyVec3,
        axis: 0 | 1 | 2 = 2
    ): vec3 {
        const voxel = this.toVoxelSpace(patientPos);
        voxel[axis] = Math.floor(voxel[axis]) + 0.5;
        return this.toWorldSpace(voxel);
    }

    /**
     * PatientSpace centre of the slice (grid Z) that `plane` cuts, or
     * undefined when the plane coincides with no slice plane — its normal
     * is not along grid Z ({@link axisAlong}): a rotated plane, or a cut
     * across an obliquely acquired grid. The slice is the one containing
     * `plane.point`, so for a displayed plane it is the slice under the
     * view centre.
     */
    sliceCenterOnPlane(plane: PlaneMm): vec3 | undefined {
        if (this.axisAlong(plane.normal) !== 2) {
            return undefined;
        }
        return this.snapPositionToSliceCenter(plane.point);
    }

    /**
     * Snap a patient-space position to the center of the voxel that
     * contains it. All three axes are snapped.
     *
     * @param patientPos position in PatientSpace
     * @returns PatientSpace position at the containing voxel's center
     */
    public snapPositionToVoxelCenter(patientPos: ReadonlyVec3): vec3 {
        const voxel = this.toVoxelSpace(patientPos);
        return this.toWorldSpace(
            vec3.fromValues(
                Math.floor(voxel[0]) + 0.5,
                Math.floor(voxel[1]) + 0.5,
                Math.floor(voxel[2]) + 0.5
            )
        );
    }

    /**
     * One scroll step from `pos` along `dir` (only its direction is used),
     * the effective voxel spacing along it ({@link voxelSpacingAlong}): slice
     * thickness on transversal, pixel width on the other standard views, a
     * smooth value between in any other direction, and never more than the
     * drawing-slab thickness ({@link voxelExtentAlong}), so every voxel stays
     * reachable. Where the plane can coincide with voxel planes — `dir` along
     * an axis of an axis-aligned grid ({@link axisAlong}) — the stepped
     * position snaps to a voxel centre, keeping the display on unblended slice
     * data after a click, jump or zoom left it between centres; a rotated view
     * has no lattice to snap to, so its step is returned as is.
     *
     * @returns The displacement from `pos` to the stepped position, in
     *   PatientSpace.
     */
    public scrollStep(pos: ReadonlyVec3, dir: ReadonlyVec3): vec3 {
        const stepMm = this.voxelSpacingAlong(dir);
        const step = vec3.scale(
            vec3.create(),
            vec3.normalize(vec3.create(), dir),
            stepMm
        );
        if (!this.isAxisAligned() || this.axisAlong(dir) === undefined) {
            return step;
        }
        const snapped = this.snapPositionToVoxelCenter(
            vec3.add(vec3.create(), pos, step)
        );
        return vec3.sub(vec3.create(), snapped, pos);
    }

    /**
     * Snap an axis-aligned mm offset (in the grid's local axes, not world
     * space) down to the nearest voxel boundary on each axis.
     *
     * Use for the lower corner of an AABB that must enclose a region: the
     * floored corner is guaranteed to be at-or-before the input on every
     * axis, so the enclosing grid never under-covers the region.
     *
     * @param pImageMm point expressed as mm offsets along the grid's X/Y/Z axes
     */
    public floorMmToVoxelBoundary(pImageMm: ReadonlyVec3): vec3 {
        return vec3.fromValues(
            Math.floor(pImageMm[0] / this.res[0]) * this.res[0],
            Math.floor(pImageMm[1] / this.res[1]) * this.res[1],
            Math.floor(pImageMm[2] / this.res[2]) * this.res[2]
        );
    }

    /**
     * Snap an axis-aligned mm offset (in the grid's local axes, not world
     * space) up to the nearest voxel boundary on each axis. The dual of
     * {@link floorMmToVoxelBoundary}; use for the upper corner of an AABB.
     *
     * @param pImageMm point expressed as mm offsets along the grid's X/Y/Z axes
     */
    public ceilMmToVoxelBoundary(pImageMm: ReadonlyVec3): vec3 {
        return vec3.fromValues(
            Math.ceil(pImageMm[0] / this.res[0]) * this.res[0],
            Math.ceil(pImageMm[1] / this.res[1]) * this.res[1],
            Math.ceil(pImageMm[2] / this.res[2]) * this.res[2]
        );
    }

    /**
     * Flat (z-major) index of the voxel containing a patient-space point, or
     * null outside the grid. The one nearest-neighbour rule behind both the
     * cursor readout and the line-profile samplers (SR-WOR-H-17-4: they must
     * agree). {@link indexToVoxelCenterWorldSpace} is its inverse.
     */
    flatIndexAt(pointMm: ReadonlyVec3): number | null {
        const voxel = this.toVoxelSpace(pointMm);
        vec3.floor(voxel, voxel);
        if (!this.includesVoxel(voxel)) {
            return null;
        }
        return (
            voxel[2] * this.sizeX * this.sizeY +
            voxel[1] * this.sizeX +
            voxel[0]
        );
    }

    /**
     * Convert a flat raster-order array index (x fastest, then y, then z) to
     * patient-space (mm) coordinates of that voxel's center.
     *
     * Used by argmax / argmin scans over voxel data — callers do the linear
     * search and pass the winning flat index here for the coordinate-frame
     * transform, so each call site doesn't reimplement the index → (x,y,z)
     * decomposition.
     */
    indexToVoxelCenterWorldSpace(flatIndex: number): vec3 {
        const sizeX = this.sizeX;
        const sliceSize = sizeX * this.sizeY;
        const z = Math.floor(flatIndex / sliceSize);
        const remainder = flatIndex % sliceSize;
        const y = Math.floor(remainder / sizeX);
        const x = remainder % sizeX;
        return this.voxelCenterToWorldSpace(vec3.fromValues(x, y, z));
    }

    /**
     * Get the normal position of the grid at a given Z index. Normal position is the DICOM coordinates offset of the slice center in z direction.\
     * For usual CT scans where the patient is aligned with the imaging device, the normal position is the same as the DICOM Image Position attribute.\
     * For an oblique scan the normal position is the distance from the first slice in the slice direction (z resolution x the slice index).
     */
    public getSliceCenterNormalPosition(zIndex: number): number {
        return vec3.dot(
            this.toWorldSpace(vec3.fromValues(0, 0, zIndex + 0.5)),
            this.getUnitZ()
        );
    }

    /**
     *
     * @returns PatientSpace coordinates of the center of the first voxel of the bottom most slice.
     * This is equal to the DICOM Image Position (Patient) attribute.
     */
    getCornerVoxelCenterPosition(): vec3 {
        return this.toWorldSpace(vec3.fromValues(0.5, 0.5, 0.5));
    }

    /**
     * @returns PatientSpace coordinates of the corner of the first voxel of the bottom most slice.
     */
    getPosition(): vec3 {
        return this.toWorldSpace(vec3.fromValues(0, 0, 0));
    }

    get position(): vec3 {
        return this.getPosition();
    }

    /**
     * Get the orientation matrix of the Grid: the rotation from the grid
     * frame to patient space (pure rotation; voxel spacing is not included).
     */
    getOrientation(): mat3 {
        return mat3.fromMat4(mat3.create(), this.transformation);
    }

    /**
     * Get the inverse of the orientation matrix of the Grid: the rotation
     * from patient space to the grid frame.
     */
    getOrientationInverse(): mat3 {
        return mat3.transpose(mat3.create(), this.getOrientation());
    }

    /**
     * Rotate a vector from Grid orientation to World (PatientSpace) orientation
     */
    public rotateToWorld(v: ReadonlyVec3): vec3 {
        return vec3.transformMat3(vec3.create(), v, this.getOrientation());
    }

    /**
     * Rotate a vector from World (PatientSpace) orientation to Grid orientation
     */
    public rotateToGrid(v: ReadonlyVec3): vec3 {
        return vec3.transformMat3(
            vec3.create(),
            v,
            this.getOrientationInverse()
        );
    }

    /** Patient-space point to mm offsets along this grid's axes from its origin. */
    public toImageMm(patientPos: ReadonlyVec3): vec3 {
        return vec3.mul(vec3.create(), this.toVoxelSpace(patientPos), this.res);
    }

    /** Inverse of {@link toImageMm}: image-aligned mm offsets back to a patient-space point. */
    public fromImageMm(pImageMm: ReadonlyVec3): vec3 {
        return this.toWorldSpace(vec3.div(vec3.create(), pImageMm, this.res));
    }

    /** Unit vector of X direction */
    getUnitX(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[0], rot[1], rot[2]);
    }

    /** Unit vector of Y direction */
    getUnitY(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[3], rot[4], rot[5]);
    }

    /** Unit vector of Z direction */
    getUnitZ(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[6], rot[7], rot[8]);
    }

    /**
     * Whether the grid axes coincide with the patient axes — i.e. the
     * orientation is a signed permutation matrix. This covers the four
     * transversal patient positions (HFS/HFP/FFS/FFP) AND 90°-rotated
     * coronal/sagittal acquisitions; it is false for genuinely oblique
     * or gantry-tilted geometry.
     *
     * Compared within tolerance, deliberately the SAME 1e-4 the
     * DICOM-level `tilted` classification applies to the raw IOP
     * cosines (dicomProcessor's ORIENTATION_TOLERANCE): the six values
     * inspected here ARE those cosines, and the two verdicts describe
     * the same series, so they must never contradict each other.
     * (Previously this compared EXACTLY against 0/±1, so grids whose
     * transformation had been rebuilt through a float32 quaternion —
     * a ~3e-8 error for 90° rotations — classified as oblique while
     * the same series reported "not tilted", and coronal-acquired
     * volumes silently rendered on fallback HFS view bases. Derived
     * grids ({@link expandOrShrink}, {@link Grid.unionAligned}) still
     * take that quaternion path, so the tolerance is required even
     * with {@link Grid.fromDicom} now preserving orientations
     * verbatim.)
     */
    isAxisAligned(): boolean {
        const AXIS_ALIGNED_TOLERANCE = 1e-4;
        // The first two columns are the DICOM row/column direction
        // cosines; the third is their cross product, so examining the
        // first six entries suffices for unit-length columns.
        const v = this.getOrientation();
        for (let i = 0; i < 6; i++) {
            const magnitude = Math.abs(v[i]);
            if (
                magnitude > AXIS_ALIGNED_TOLERANCE &&
                Math.abs(magnitude - 1) > AXIS_ALIGNED_TOLERANCE
            ) {
                return false;
            }
        }
        return true;
    }

    /**
     * The grid axis parallel to `patientDir` (either sign) within the
     * frame-alignment tolerance, or undefined when no axis is — an
     * obliquely acquired grid, or a direction between axes. Unlike
     * {@link isAxisAligned}, which asks whether the grid lines up with the
     * patient frame as a whole, this identifies WHICH axis lies along a
     * given direction: on a coronal acquisition patient Z runs along grid
     * Y, not grid Z.
     *
     * @param patientDir Direction in PatientSpace; need not be unit length.
     * @throws if `patientDir` has zero length.
     */
    axisAlong(patientDir: ReadonlyVec3): SliceAxis | undefined {
        if (!(vec3.length(patientDir) > 0)) {
            throw new Error('axisAlong: direction must have non-zero length');
        }
        const unit = vec3.normalize(vec3.create(), patientDir);
        const axes: [ReadonlyVec3, ReadonlyVec3, ReadonlyVec3] = [
            this.getUnitX(),
            this.getUnitY(),
            this.getUnitZ(),
        ];
        for (let axis = 0; axis < 3; ++axis) {
            // A wider tolerance than isAxisAligned uses, so any grid that
            // counts as axis-aligned has an axis found here for each patient
            // axis.
            if (areParallelDirections(unit, axes[axis])) {
                return axis as SliceAxis;
            }
        }
        return undefined;
    }

    toRotatedBoundingBox(): RotatedBoundingBox {
        const orientation = this.getOrientation();
        const invOrientation = this.getOrientationInverse();
        // RotatedBoundingBox stores its BoundingBox in local (image) space
        // and applies the rotation to go to patient/world space.
        // getPosition() returns world-space coordinates (rotation already
        // applied), so we must undo the rotation to get local-space coords.
        const localMin = vec3.transformMat3(
            vec3.create(),
            this.getPosition(),
            invOrientation
        );
        const localMax = vec3.add(vec3.create(), localMin, this.getSizeMM());
        return new RotatedBoundingBox(
            new BoundingBox(localMin, localMax),
            orientation
        );
    }

    /**
     * Oriented-box corner vertices in patient space.
     * Order matches BoundingBox.getVertices() when axis-aligned:
     * 0: (0,0,0)
     * 1: (X,0,0)
     * 2: (X,Y,0)
     * 3: (0,Y,0)
     * 4: (0,0,Z)
     * 5: (X,0,Z)
     * 6: (X,Y,Z)
     * 7: (0,Y,Z)
     * Note that the vertex coordinates are voxel corners, not centers.
     * Texture coordinates are 0 and 1 at corners, so linear interpolation
     * produces correct voxel-center samples (0.5/size, 1.5/size, etc.).
     */
    getVerticesWithTexture(): Vertex[] {
        const { sizeX, sizeY, sizeZ } = this;

        const make = (dx: 0 | 1, dy: 0 | 1, dz: 0 | 1): Vertex => {
            const worldPos = this.toWorldSpace(
                vec3.fromValues(dx * sizeX, dy * sizeY, dz * sizeZ)
            );
            return new Vertex(worldPos, vec3.fromValues(dx, dy, dz));
        };

        return [
            make(0, 0, 0),
            make(0, 1, 0),
            make(1, 1, 0),
            make(1, 0, 0),
            make(0, 0, 1),
            make(0, 1, 1),
            make(1, 1, 1),
            make(1, 0, 1),
        ];
    }

    getEdgesWithTexture(): Edge[] {
        const v = this.getVerticesWithTexture();
        if (v.length !== 8) {
            return [];
        }
        const edges: Edge[] = [];
        // bottom
        edges.push(
            new Edge(v[0], v[1]),
            new Edge(v[1], v[2]),
            new Edge(v[2], v[3]),
            new Edge(v[3], v[0])
        );
        // vertical
        edges.push(
            new Edge(v[0], v[4]),
            new Edge(v[1], v[5]),
            new Edge(v[2], v[6]),
            new Edge(v[3], v[7])
        );
        // top
        edges.push(
            new Edge(v[4], v[5]),
            new Edge(v[5], v[6]),
            new Edge(v[6], v[7]),
            new Edge(v[7], v[4])
        );
        return edges;
    }

    /**
     * The projection {@link crossSection} takes to cut a shape by layer
     * `z` of this grid: PatientSpace to a space where the grid's X and Y
     * extent spans [-1, 1] and Z is the signed distance in mm from the
     * layer's center plane. Drawing that cut into layer `z` of a texture
     * on this grid covers the voxels of the layer the shape reaches.
     *
     * @param z Index of the layer along the grid's Z axis
     */
    worldToLayerProjection(z: number): mat4 {
        const layerCenterClipZ = (2 * z + 1) / this.sizeZ - 1;
        const toLayer = mat4.fromValues(
            1,
            0,
            0,
            0,
            0,
            1,
            0,
            0,
            0,
            0,
            this.sizeZMM / 2,
            0,
            0,
            0,
            (-layerCenterClipZ * this.sizeZMM) / 2,
            1
        );
        return mat4.multiply(toLayer, toLayer, this._worldToClipSpace);
    }

    /**
     * Cross section of oriented grid box with a projection plane.
     * @param toProjection plane transform matrix
    /**
     * Computes the cross-section geometry data for this grid with a projection plane.
     * Returns the vertex coordinates, texture coordinates, and triangle indices needed for rendering.
     * 
     * @param toProjection Transformation matrix from world space to projection space
     * @returns Object containing vertex data and indices, or undefined if no intersection
     */
    crossSectionGeometry(toProjection: ReadonlyMat4):
        | {
              vertexCoordinates: number[];
              textureCoordinates: number[];
              indices: number[];
          }
        | undefined {
        const intersections: Vertex[] = [];
        for (const edge of this.getEdgesWithTexture()) {
            const intersection = edge.intersection(toProjection, false);
            if (intersection) {
                intersections.push(intersection);
            }
        }
        const vertexCoordinates: number[] = [];
        const textureCoordinates: number[] = [];
        const indices: number[] = [];
        if (intersections.length > 0) {
            const buffers = VerticesToArrays(intersections);
            indices.push(
                ...tesselateConvexPolygon(
                    buffers[0],
                    vertexCoordinates.length / 2
                )
            );
            vertexCoordinates.push(...buffers[0]);
            textureCoordinates.push(...buffers[1]);
        }
        if (indices.length === 0) {
            return undefined;
        }
        return { vertexCoordinates, textureCoordinates, indices };
    }

    /**
     * Cross section of oriented grid box with a projection plane.
     * Creates a VertexArrayObject ready for WebGL rendering.
     *
     * @param toProjection Transformation matrix from world space to projection space
     * @returns VertexArrayObject ready for rendering, or undefined if no intersection
     */
    crossSection(toProjection: ReadonlyMat4): VertexArrayObject | undefined {
        const geometry = this.crossSectionGeometry(toProjection);
        if (!geometry) {
            return undefined;
        }
        const vao = new VertexArrayObject();
        vao.bind();
        vao.createAttribute(0, gl.FLOAT, geometry.vertexCoordinates, 2);
        vao.createAttribute(1, gl.FLOAT, geometry.textureCoordinates, 3);
        vao.createIndexBuffer(geometry.indices);
        vao.unbind();
        return vao;
    }

    /**
     * The whole slices to add below (`before`) and above (`after`) this grid
     * so it holds its slice `sliceZ` and, where `bounds` has room, the slice
     * past it: none for a slice it holds. `undefined` when `sliceZ` is no
     * slice of `bounds` or this grid's slices do not lie on those of `bounds`.
     */
    public zExtensionToHold(
        sliceZ: number,
        bounds: Grid
    ): { before: number; after: number } | undefined {
        if (sliceZ >= 0 && sliceZ < this.sizeZ) {
            return { before: 0, after: 0 };
        }
        const originZ = this.originInVoxelsOf(bounds)?.[2] ?? NaN;
        const offset = Math.round(originZ);
        // Slice k of `bounds` is slice k - offset of this grid.
        const [first, last] = [-offset, bounds.sizeZ - 1 - offset];
        if (!(Math.abs(originZ - offset) <= 0.01)) {
            return undefined;
        }
        if (sliceZ < first || sliceZ > last) {
            return undefined;
        }
        return {
            before: Math.max(0, -Math.max(sliceZ - 1, first)),
            after: Math.max(0, Math.min(sliceZ + 1, last) - this.sizeZ + 1),
        };
    }

    /**
     * Creates a new Grid by expanding or shrinking this grid in any direction.
     * Positive values expand the grid, negative values shrink it.
     * The new grid maintains the same resolution and orientation as the original.
     *
     * @param expansionX - Expansion in X direction (in voxels). Positive expands, negative shrinks.
     * @param expansionY - Expansion in Y direction (in voxels). Positive expands, negative shrinks.
     * @param expansionZ - Expansion in Z direction (in voxels). Positive expands, negative shrinks.
     * @param centerExpansion - If true, expands/shrinks equally on both sides. If false, only expands/shrinks in positive direction.
     * @returns New Grid with modified dimensions
     * @throws Error if resulting grid would have negative or zero dimensions
     */
    public expandOrShrink(
        expansionX: number,
        expansionY: number,
        expansionZ: number,
        centerExpansion: boolean = true
    ): Grid {
        assert(Number.isInteger(expansionX), 'X expansion must be integer');
        assert(Number.isInteger(expansionY), 'Y expansion must be integer');
        assert(Number.isInteger(expansionZ), 'Z expansion must be integer');

        // Calculate new dimensions
        let newSizeX: number;
        let newSizeY: number;
        let newSizeZ: number;
        let offsetX: number;
        let offsetY: number;
        let offsetZ: number;

        if (centerExpansion) {
            // Expand/shrink equally on both sides
            newSizeX = this.sizeX + 2 * expansionX;
            newSizeY = this.sizeY + 2 * expansionY;
            newSizeZ = this.sizeZ + 2 * expansionZ;
            offsetX = -expansionX;
            offsetY = -expansionY;
            offsetZ = -expansionZ;
        } else {
            // Expand/shrink only in positive direction
            newSizeX = this.sizeX + expansionX;
            newSizeY = this.sizeY + expansionY;
            newSizeZ = this.sizeZ + expansionZ;
            offsetX = 0;
            offsetY = 0;
            offsetZ = 0;
        }

        // Validate new dimensions
        if (newSizeX <= 0 || newSizeY <= 0 || newSizeZ <= 0) {
            throw new Error(
                `Resulting grid dimensions would be invalid: ${newSizeX}x${newSizeY}x${newSizeZ}. ` +
                    'All dimensions must be positive.'
            );
        }

        const localDeltaMm = vec3.fromValues(
            offsetX * this.resX,
            offsetY * this.resY,
            offsetZ * this.resZ
        );

        const worldDelta = vec3.transformMat3(
            vec3.create(),
            localDeltaMm,
            this.getOrientation()
        );

        const newPosition = vec3.add(
            vec3.create(),
            this.getPosition(),
            worldDelta
        );

        // Create new transformation matrix with updated position
        const newTransformation = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), this.getOrientation()),
            newPosition
        );

        return new Grid(
            newSizeX,
            newSizeY,
            newSizeZ,
            this.resX,
            this.resY,
            this.resZ,
            newTransformation
        );
    }

    /**
     * Whether this grid and `other` sample space the same way — i.e.
     * share the same orientation and voxel resolution within tolerance.
     * This is the precondition for mapping one grid's voxel indices
     * onto the other by a constant integer offset (e.g. extracting an
     * image slice aligned to a structure texture, or any direct
     * row-by-row copy between the two rasters).
     *
     * Unlike {@link hasEqualGeometryTo} this does NOT require equal size
     * or position, so it holds for a sub-grid of a larger grid. The
     * caller remains responsible for checking the translational
     * (sub-voxel) offset wherever exact index alignment is required.
     *
     * @param other grid to compare sampling against
     * @param orientationTol max per-element orientation matrix
     *        difference (default 1e-6)
     * @param resTol max per-axis resolution difference in mm
     *        (default 1e-3)
     */
    public hasSameSamplingAs(
        other: Grid,
        orientationTol = 1e-6,
        resTol = 1e-3
    ): boolean {
        const oA = this.getOrientation();
        const oB = other.getOrientation();
        for (let i = 0; i < 9; i++) {
            if (Math.abs(oA[i] - oB[i]) > orientationTol) {
                return false;
            }
        }
        const rA = this.getRes();
        const rB = other.getRes();
        return (
            Math.abs(rA[0] - rB[0]) <= resTol &&
            Math.abs(rA[1] - rB[1]) <= resTol &&
            Math.abs(rA[2] - rB[2]) <= resTol
        );
    }

    /**
     * This grid's origin in `other`'s voxel coordinates, or `undefined`
     * when the two do not sample space the same way.
     */
    public originInVoxelsOf(other: Grid): vec3 | undefined {
        if (!this.hasSameSamplingAs(other)) {
            return undefined;
        }
        return other.toVoxelSpace(this.getPosition());
    }

    /**
     * Instance method to check if this grid has identical geometry to another grid.
     * Returns true if grids have the same size, resolution, position, and orientation
     * within defined tolerances.
     *
     * Tolerances (chosen for typical DICOM floating-point rounding):
     * - Resolution: 0.001 mm (sub-voxel precision)
     * - Position:   0.01  mm
     * - Orientation: 1e-6
     *
     * @param b The grid to compare against
     * @returns true if grids have identical geometry, false otherwise
     */
    public hasEqualGeometryTo(b: Grid): boolean {
        const RES_TOL = 0.001;
        const POS_TOL = 0.01;
        const ORI_TOL = 1e-6;

        // Grid size — exact integer match
        const aSize = this.getSize();
        const bSize = b.getSize();
        if (
            aSize[0] !== bSize[0] ||
            aSize[1] !== bSize[1] ||
            aSize[2] !== bSize[2]
        ) {
            return false;
        }

        // Voxel resolution (mm)
        const aRes = this.getRes();
        const bRes = b.getRes();
        if (
            Math.abs(aRes[0] - bRes[0]) > RES_TOL ||
            Math.abs(aRes[1] - bRes[1]) > RES_TOL ||
            Math.abs(aRes[2] - bRes[2]) > RES_TOL
        ) {
            return false;
        }

        // Grid position: corner of first voxel in patient space (mm)
        const aPos = this.getPosition();
        const bPos = b.getPosition();
        if (
            Math.abs(aPos[0] - bPos[0]) > POS_TOL ||
            Math.abs(aPos[1] - bPos[1]) > POS_TOL ||
            Math.abs(aPos[2] - bPos[2]) > POS_TOL
        ) {
            return false;
        }

        // Orientation
        const aOri = this.getOrientation();
        const bOri = b.getOrientation();
        for (let i = 0; i < 9; i++) {
            if (Math.abs(aOri[i] - bOri[i]) > ORI_TOL) {
                return false;
            }
        }

        return true;
    }
}
